import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from '@/components/ui/button';
import HtmlDescription from '@/components/HtmlDescription';
import { serviceService, OnlineService } from './serviceService';
import { addToCart } from '../cart/cartSlice';
import { cartService } from '../cart/cartService';
import ServicePlanSelector, { planDurationLabel, planTierLabel } from './ServicePlanSelector';

const formatPrice = (p: number) => `${p.toLocaleString('ru-RU')} ₽`;

const CATEGORY_LABEL: Record<string, string> = {
  GameService: 'Игровой сервис',
  Entertainment: 'Развлечения',
  AI: 'AI',
  Work: 'Для работы',
  Other: 'Сервис',
};

const ServicePage = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const [service, setService] = useState<OnlineService | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (!id || Number.isNaN(Number(id))) {
      setError('Некорректный ID сервиса');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setAdded(false);
    serviceService
      .fetch(Number(id))
      .then((s) => {
        setService(s);
        const firstPlan = s.plans[0];
        setSelectedId(firstPlan?.id ?? s.id);
      })
      .catch(() => setError('Не удалось загрузить сервис'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleAdd = async () => {
    if (!service) return;
    const pid = selectedId ?? service.id;
    try {
      await cartService.add(pid);
      dispatch(addToCart(pid));
      setAdded(true);
    } catch {
      /* ignore — user can retry */
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background px-4 py-10 text-foreground">
        <div className="mx-auto max-w-[90rem] animate-pulse">
          <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
            <div className="aspect-square sm:aspect-[5/6] rounded-[32px] bg-foreground/5" />
            <div className="space-y-4">
              <div className="h-10 w-2/3 rounded bg-foreground/5" />
              <div className="h-24 w-full rounded-2xl bg-foreground/5" />
              <div className="h-14 w-full rounded-2xl bg-foreground/5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-background px-4 py-16 text-foreground">
        <div className="mx-auto max-w-xl text-center">
          <p className="mb-2 text-lg font-medium">Сервис не найден</p>
          <p className="mb-6 text-foreground/50">{error}</p>
          <Link to="/">
            <Button variant="destructive" className="px-6 py-3">На главную</Button>
          </Link>
        </div>
      </div>
    );
  }

  const plans = service.plans;
  const selected = plans.find((p) => p.id === selectedId);
  const activePrice = selected?.price ?? service.price;
  const activePicture = selected?.picture || service.picture;

  return (
    <div className="min-h-screen overflow-x-hidden bg-background py-6 text-foreground sm:py-10">
      <div className="mx-auto max-w-[90rem] px-4 sm:px-6">

        {/* Breadcrumb */}
        <nav className="mb-6 flex items-center gap-2 text-sm text-foreground/50">
          <Link to="/" className="transition-colors hover:text-foreground">Главная</Link>
          <span>·</span>
          <span className="truncate text-foreground/70">{service.name}</span>
        </nav>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr] xl:grid-cols-[420px_1fr] xl:gap-10">

          {/* Cover */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="overflow-hidden rounded-[32px] border border-foreground/10 shadow-[0_32px_80px_rgba(0,0,0,0.1)]">
              <img
                src={activePicture}
                alt={service.name}
                className="aspect-square sm:aspect-[5/6] w-full object-cover"
                loading="eager"
              />
            </div>
          </div>

          {/* Info + buy */}
          <div className="flex flex-col gap-5">
            <div>
              {service.category && (
                <span className="inline-flex items-center rounded-full border border-foreground/10 bg-foreground/6 px-3 py-1 text-xs font-medium text-foreground/70">
                  {CATEGORY_LABEL[service.category] ?? service.category}
                </span>
              )}
              <h1 className="mt-4 text-3xl font-black leading-[1.08] tracking-[-0.035em] sm:text-4xl xl:text-5xl">
                {service.name}
              </h1>
              {service.platforms.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {service.platforms.map((p) => (
                    <span key={p} className="rounded-full border border-foreground/10 bg-foreground/6 px-3 py-1 text-xs text-foreground/70">
                      {p}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Plans + buy */}
            <div className="rounded-[28px] border border-foreground/10 bg-card p-5 shadow-sm sm:p-7">
              {selected && (
                <ServicePlanSelector plans={plans} selected={selected}
                  onSelect={planId => { setSelectedId(planId); setAdded(false); }} />
              )}

              <div className="mt-6 border-t border-foreground/10 pt-6" aria-live="polite" aria-atomic="true">
                <p className="text-xs font-semibold text-foreground/40">Стоимость выбранного варианта</p>
                {selected && <p className="mt-2 text-sm font-medium text-foreground/70">
                  {planTierLabel(selected)}{selected.durationMonths ? ` · ${planDurationLabel(selected.durationMonths)}` : ''}
                </p>}
                <p className="mt-1 text-4xl font-black tracking-[-0.035em] sm:text-[2.75rem]">{formatPrice(activePrice)}</p>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                {added ? (
                  <>
                    <span className="inline-flex items-center rounded-2xl border border-green-500/30 bg-green-500/10 px-5 py-3 text-sm font-medium text-green-400">
                      Добавлено в корзину
                    </span>
                    <Link to="/cart">
                      <Button variant="ghost" className="rounded-2xl border border-foreground/10 bg-foreground/5 px-5 py-3 text-sm">
                        Перейти в корзину
                      </Button>
                    </Link>
                  </>
                ) : (
                  <Button
                    onClick={handleAdd}
                    variant="destructive"
                    className="rounded-2xl px-8 py-3 text-base font-semibold"
                  >
                    В корзину
                  </Button>
                )}
              </div>
            </div>

            {/* Description */}
            {(selected?.description || service.description) && (
              <div className="rounded-[28px] border border-foreground/10 bg-card p-6">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-foreground/40">Описание</p>
                <HtmlDescription
                  html={selected?.description || service.description}
                  className="text-sm leading-relaxed text-foreground/80"
                />
              </div>
            )}

            {/* Screenshots */}
            {service.screenshots.length > 0 && (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {service.screenshots.map((src, i) => (
                  <img key={i} src={src} alt="" className="aspect-video w-full rounded-2xl object-cover" loading="lazy" />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
